import React, { useState } from 'react';
import ConfirmModal from './ConfirmModal';
import CloseButton from './CloseButton';
import SchoolDataForm, { SchoolDataValue } from '../school/SchoolDataForm';
import { useLoading } from '../../context/LoadingContext';
import {
  SCHOOL_YEAR_PROMOTED,
  SchoolYearNotification,
  StudentSchoolData,
  getSchoolYearLabel,
  resolveSchoolYearForStudent,
  saveStudentSchool,
} from '../../utils/school';

// Il tipo vive in utils/school insieme alla logica: ri-esportato per i chiamanti storici.
export type { SchoolYearNotification };

/** 'accepted' = dati confermati; 'ignored' = rimandato, lo studente resta da sistemare. */
export type SchoolYearOutcome = 'accepted' | 'ignored';

interface SchoolYearModalProps {
  /** Assente quando la modale è aperta per completare dati mancanti senza notifica. */
  notification?: SchoolYearNotification | null;
  student: StudentSchoolData & { id: number; first_name: string };
  /** Modale chiusa: il chiamante decide cosa fare in base all'esito. */
  onDone: (outcome: SchoolYearOutcome) => void;
}

const emptyValue: SchoolDataValue = {
  school_id: null,
  school_class: '',
  dirty: false,
  valid: false,
  editing: false,
};

const SchoolYearModal: React.FC<SchoolYearModalProps> = ({ notification, student, onDone }) => {
  const [value, setValue] = useState<SchoolDataValue>(emptyValue);
  const [showIgnoreWarning, setShowIgnoreWarning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // L'ErrorModal globale sta sotto questa modale: gli errori vanno mostrati qui dentro.
  const [errorMessage, setErrorMessage] = useState('');
  const { setLoading } = useLoading();

  const isPromoted = notification?.type === SCHOOL_YEAR_PROMOTED;

  // Nel riepilogo di promozione il wizard di modifica ha i propri "Annulla" e
  // "Fine": i pulsanti della modale spariscono finché è aperto.
  const isEditingSchool = isPromoted && value.editing;

  // In promozione i dati sono già compilati: se la famiglia non cambia nulla
  // la conferma resta possibile anche se l'indirizzo non è risolvibile.
  const canConfirm = isPromoted ? !value.editing && (value.valid || !value.dirty) : value.valid;

  const handleConfirm = async () => {
    if (!canConfirm || submitting) return;
    setSubmitting(true);
    setLoading(true);
    setErrorMessage('');
    try {
      if (value.dirty && value.valid) {
        await saveStudentSchool(student.id, {
          school_id: value.school_id,
          school_class: value.school_class,
          // In promozione le materie già associate restano; ricompilando si azzerano.
          subject_ids: isPromoted ? undefined : [],
        });
      }
      // Si chiudono tutte le notifiche pendenti dello studente, non solo questa:
      // il job ne rigenera una per batch e le vecchie lo terrebbero bloccato.
      if (notification) {
        await resolveSchoolYearForStudent(student.id, 'accept');
      }
      onDone('accepted');
    } catch (error: any) {
      console.error('Errore nella conferma dei dati scuola:', error);
      setErrorMessage(error.response?.data?.message || 'Errore durante il salvataggio. Riprova.');
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const handleIgnore = async () => {
    if (submitting) return;
    setSubmitting(true);
    setLoading(true);
    setErrorMessage('');
    try {
      // Anche qui su tutti i duplicati, altrimenti la modale si riapre per i gemelli.
      if (notification) {
        await resolveSchoolYearForStudent(student.id, 'read');
      }
      setShowIgnoreWarning(false);
      onDone('ignored');
    } catch (error: any) {
      console.error('Errore nel segnare la notifica come letta:', error);
      setShowIgnoreWarning(false);
      setErrorMessage(error.response?.data?.message || 'Errore durante il salvataggio. Riprova.');
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="z-[70] fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 p-4">
        <div className="bg-white rounded-xl p-6 shadow-2xl max-w-lg mx-4 w-full max-h-[90vh] overflow-y-auto flex flex-col gap-4">
          <div className="flex items-center gap-2">
            {/* pl-14 compensa larghezza della X e gap, così il titolo resta centrato */}
            <h2 className="text-fpcred font-bold text-xl text-center w-full pl-14">
              {isPromoted
                ? `Nuovo anno scolastico ${getSchoolYearLabel(notification?.metadata.school_year)}`.trim()
                : 'Dati scuola da aggiornare'}
            </h2>
            {/* Chiude rimandando: stesso effetto del vecchio pulsante "Ignora". */}
            <CloseButton
              className={isEditingSchool ? 'invisible' : ''}
              onClick={() => setShowIgnoreWarning(true)}
              disabled={submitting || isEditingSchool}
            />
          </div>
          <p className="text-center text-gray-700">
            {isPromoted ? (
              <>
                Abbiamo aggiornato la classe di <strong>{student.first_name}</strong>.
                <br />
                Controlla che le informazioni siano corrette
              </>
            ) : (
              <>
                Aggiorna le informazioni di <strong>{student.first_name}</strong>.
              </>
            )}
          </p>

          <SchoolDataForm
            initial={isPromoted ? student : null}
            variant={isPromoted ? 'summary' : 'wizard'}
            // Nel wizard la conferma vive nella riga di navigazione, all'ultimo step.
            submit={
              isPromoted
                ? undefined
                : { label: 'Conferma', onClick: handleConfirm, disabled: !canConfirm || submitting }
            }
            onChange={setValue}
          />

          {errorMessage && <p className="text-center text-fpcred font-semibold">{errorMessage}</p>}

          {isPromoted && !isEditingSchool && (
            <button
              type="button"
              className={`font-bold rounded-lg w-full p-2 text-white transition-colors ${
                canConfirm && !submitting ? 'bg-fpcred hover:bg-buttonhover' : 'bg-gray-300 cursor-not-allowed'
              }`}
              onClick={handleConfirm}
              disabled={!canConfirm || submitting}>
              Conferma
            </button>
          )}
        </div>
      </div>

      <ConfirmModal
        open={showIgnoreWarning}
        title="Attenzione"
        message={
          isPromoted
            ? `Senza confermare i dati della scuola non potrai prenotare lezioni per ${student.first_name}. Potrai farlo al momento della prenotazione.`
            : `Senza i dati della scuola non potrai prenotare lezioni per ${student.first_name}. Potrai completarli in seguito, al momento della prenotazione.`
        }
        confirmLabel="Ho capito"
        cancelLabel="Torna indietro"
        onConfirm={handleIgnore}
        onCancel={() => setShowIgnoreWarning(false)}
        zIndexClass="z-[80]"
      />
    </>
  );
};

export default SchoolYearModal;
